import { useParams, Link } from 'react-router-dom';
import SEOMeta from '@/components/feature/SEOMeta';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import MovieCard from '@/components/feature/MovieCard';
import { getMovieBySlug, getMoviesByCategory, categories, movies } from '@/mocks/movies';

export default function Detail() {
  const { id } = useParams<{ id: string }>();
  const movie = getMovieBySlug(id || '');
  const relatedMovies = movie
    ? getMoviesByCategory(movie.categorySlug).filter((m) => m.id !== movie.id).slice(0, 6)
    : [];
  const hotMovies = movies.filter((m) => m.isHot && m.id !== movie?.id).slice(0, 6);

  if (!movie) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <main className="pt-16 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <span className="w-20 h-20 flex items-center justify-center mx-auto rounded-full bg-secondary-100 text-secondary-500 mb-4">
              <i className="ri-error-warning-line text-3xl"></i>
            </span>
            <h1 className="text-xl font-heading font-bold text-foreground-900 mb-2">影片未找到</h1>
            <p className="text-sm text-foreground-500 mb-6">抱歉，您要查找的影片不存在或已下架</p>
            <Link to="/" className="inline-flex items-center gap-1.5 px-6 py-2.5 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-full text-sm font-medium transition-colors whitespace-nowrap">
              <i className="ri-arrow-left-line"></i>
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const catName = categories.find((c) => c.slug === movie.categorySlug)?.name || movie.category;

  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'Movie',
    name: movie.title,
    description: movie.description,
    image: movie.coverImage,
    dateCreated: String(movie.year),
    director: {
      '@type': 'Person',
      name: movie.director,
    },
    actor: movie.cast.map((name) => ({
      '@type': 'Person',
      name,
    })),
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: String(movie.rating),
      bestRating: '10',
      ratingCount: Math.floor(Math.random() * 50000) + 5000,
    },
  };

  return (
    <>
      <ScrollToTop />
      <SEOMeta
        title={`${movie.title} - 农民影视 | 免费高清在线观看`}
        description={movie.description.slice(0, 150)}
        keywords={`${movie.title},农民影视,免费观看,${catName},在线观看,${movie.tags.join(',')}`}
        canonicalUrl={`/detail/${movie.slug}`}
        ogTitle={`${movie.title} - 农民影视`}
        ogDescription={movie.description.slice(0, 150)}
        ogImage={movie.coverImage}
        ogType="video.movie"
      />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
      />

      <Navbar />

      <main className="pt-16">
        <Breadcrumb />

        <section className="w-full px-4 md:px-6 py-8">
          <div className="max-w-[1280px] mx-auto">
            <div className="flex flex-col lg:flex-row gap-8">
              <div className="flex-shrink-0">
                <div className="w-48 md:w-56 aspect-[2/3] rounded-xl overflow-hidden bg-background-200">
                  <img
                    src={movie.coverImage}
                    alt={movie.title}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>

              <div className="flex-1 min-w-0">
                <article>
                  <h1 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-3">
                    {movie.title}
                  </h1>

                  <div className="flex flex-wrap items-center gap-3 mb-4">
                    <span className="flex items-center gap-1 px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-sm font-semibold whitespace-nowrap">
                      <i className="ri-star-fill text-sm"></i>
                      {movie.rating} 分
                    </span>
                    <span className="text-sm text-foreground-500">{movie.year}</span>
                    <span className="text-sm text-foreground-400">·</span>
                    <span className="text-sm text-foreground-500">{movie.duration}</span>
                    <span className="text-sm text-foreground-400">·</span>
                    <Link
                      to={`/category/${movie.categorySlug}`}
                      className="text-sm text-primary-600 hover:text-primary-700 font-medium whitespace-nowrap"
                    >
                      {catName}
                    </Link>
                  </div>

                  <div className="flex flex-wrap gap-1.5 mb-5">
                    {movie.tags.map((tag) => (
                      <span key={tag} className="px-2.5 py-1 text-xs rounded-lg bg-secondary-100 text-secondary-700 whitespace-nowrap">
                        {tag}
                      </span>
                    ))}
                  </div>

                  <div className="space-y-2.5 mb-6 text-sm">
                    <div className="flex items-start gap-3">
                      <span className="text-foreground-500 flex-shrink-0 w-12 whitespace-nowrap">导演：</span>
                      <span className="text-foreground-800 font-medium">{movie.director}</span>
                    </div>
                    {movie.cast.length > 0 && (
                      <div className="flex items-start gap-3">
                        <span className="text-foreground-500 flex-shrink-0 w-12 whitespace-nowrap">主演：</span>
                        <span className="text-foreground-800">{movie.cast.join(' / ')}</span>
                      </div>
                    )}
                    <div className="flex items-start gap-3">
                      <span className="text-foreground-500 flex-shrink-0 w-12 whitespace-nowrap">类型：</span>
                      <span className="text-foreground-800">{catName}</span>
                    </div>
                    {movie.type === 'tv' && movie.tvEpisodes && (
                      <div className="flex items-start gap-3">
                        <span className="text-foreground-500 flex-shrink-0 w-12 whitespace-nowrap">集数：</span>
                        <span className="text-foreground-800">{movie.tvEpisodes}集</span>
                      </div>
                    )}
                  </div>

                  <div className="flex flex-wrap gap-3">
                    <button className="inline-flex items-center gap-2 px-8 py-3 bg-primary-500 hover:bg-primary-600 text-background-50 font-medium rounded-full transition-colors text-sm whitespace-nowrap">
                      <i className="ri-play-circle-line text-lg"></i>
                      立即播放
                    </button>
                    <button className="inline-flex items-center gap-2 px-6 py-3 bg-background-100 hover:bg-background-200 text-foreground-700 font-medium rounded-full border border-background-200/70 transition-colors text-sm whitespace-nowrap">
                      <i className="ri-heart-line"></i>
                      收藏
                    </button>
                    <button className="inline-flex items-center gap-2 px-6 py-3 bg-background-100 hover:bg-background-200 text-foreground-700 font-medium rounded-full border border-background-200/70 transition-colors text-sm whitespace-nowrap">
                      <i className="ri-share-line"></i>
                      分享
                    </button>
                  </div>

                  <div className="mt-6 p-5 rounded-xl bg-background-100 border border-background-200/60">
                    <h2 className="text-base font-heading font-semibold text-foreground-900 mb-3">剧情简介</h2>
                    <p className="text-sm text-foreground-700 leading-relaxed">{movie.description}</p>
                  </div>
                </article>
              </div>
            </div>
          </div>
        </section>

        {relatedMovies.length > 0 && (
          <section className="w-full px-4 md:px-6 py-12 bg-background-100">
            <div className="max-w-[1280px] mx-auto">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="text-lg font-heading font-bold text-foreground-900">同类推荐</h3>
                  <p className="text-sm text-foreground-500 mt-0.5">更多{catName}影片</p>
                </div>
                <Link
                  to={`/category/${movie.categorySlug}`}
                  className="text-sm text-primary-600 hover:text-primary-700 font-medium whitespace-nowrap"
                >
                  查看全部
                  <i className="ri-arrow-right-line ml-1"></i>
                </Link>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
                {relatedMovies.map((m) => (
                  <MovieCard key={m.id} movie={m} />
                ))}
              </div>
            </div>
          </section>
        )}

        <section className="w-full px-4 md:px-6 py-12 bg-background-50">
          <div className="max-w-[1280px] mx-auto">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-lg font-heading font-bold text-foreground-900">热门推荐</h3>
                <p className="text-sm text-foreground-500 mt-0.5">大家都在看</p>
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
              {hotMovies.map((m) => (
                <MovieCard key={m.id} movie={m} />
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}